import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { digitsOnly, groupThousands } from '../../../common/utils/money';
import { MAGNITUDES } from '../../../core/constants/billing';
import { useCreateCost, useUpdateCost } from '../contexts/costs';

import '../styles/billing.css';

function MoneyInput({ value, onChange, disabled }) {
    return (
        <span className="billing-money">
            <span className="billing-money__sign">$</span>
            <input type="text" inputMode="numeric" value={groupThousands(value)} onChange={(e) => onChange(digitsOnly(e.target.value))} disabled={disabled} placeholder="0" />
        </span>
    );
}

// Rubro de costo (admin-billing-catalog.md). `amount` en COP; `quantitative` marca
// si escala por cantidad (× quantity de la composición) y su `magnitude` es la unidad.
export default function CostForm({ cost, onClose, onSaved }) {
    const editing = !!cost;
    const { mutateAsync: create, isPending: creating } = useCreateCost();
    const { mutateAsync: update, isPending: updating } = useUpdateCost(cost?.id);
    const busy = creating || updating;
    const cardRef = useModalDialog(onClose);

    const [name, setName] = useState(cost?.name || '');
    const [amount, setAmount] = useState(cost?.amount != null ? digitsOnly(String(cost.amount)) : '');
    const [quantitative, setQuantitative] = useState(!!cost?.quantitative);
    const [magnitude, setMagnitude] = useState(cost?.magnitude || '');
    const [remarks, setRemarks] = useState(typeof cost?.remarks === 'string' ? cost.remarks : '');

    const submit = async () => {
        if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
        if (amount === '' || amount == null) { toast.error('El monto es obligatorio'); return; }
        const payload = { name: name.trim(), amount: Number(amount), quantitative };
        if (quantitative && magnitude) payload.magnitude = magnitude;
        if (remarks.trim()) payload.remarks = remarks.trim();
        try {
            if (editing) await update(payload); else await create(payload);
            toast.success(editing ? 'Costo actualizado' : 'Costo creado');
            onSaved?.();
        } catch (er) {
            if (er?.status === 409) toast.error('Ya existe un costo activo con ese nombre');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo guardar el costo');
        }
    };

    return (
        <div className="billing-modal" role="dialog" aria-modal="true" aria-label={editing ? 'Editar costo' : 'Nuevo costo'}>
            <div className="billing-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="billing-modal__card" ref={cardRef}>
                <header className="billing-modal__head">
                    <h2 className="billing-modal__title">{editing ? 'Editar costo' : 'Nuevo costo'}</h2>
                    <button type="button" className="billing-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="billing-form">
                    <label className="billing-form__field">
                        <span className="billing-form__label">Nombre</span>
                        <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
                    </label>

                    <div className="billing-form__row">
                        <label className="billing-form__field">
                            <span className="billing-form__label">Monto (COP)</span>
                            <MoneyInput value={amount} onChange={setAmount} disabled={busy} />
                        </label>
                        <label className="billing-form__field billing-form__field--check">
                            <input type="checkbox" checked={quantitative} onChange={(e) => setQuantitative(e.target.checked)} disabled={busy} />
                            <span>Escala por cantidad</span>
                        </label>
                    </div>

                    {quantitative && (
                        <label className="billing-form__field">
                            <span className="billing-form__label">Magnitud</span>
                            <select value={magnitude} onChange={(e) => setMagnitude(e.target.value)} disabled={busy}>
                                <option value="">Sin especificar</option>
                                {MAGNITUDES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </label>
                    )}

                    <label className="billing-form__field">
                        <span className="billing-form__label">Notas</span>
                        <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} placeholder="Opcional" />
                    </label>
                </div>

                <footer className="billing-modal__foot">
                    <button type="button" className="billing-btn billing-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="billing-btn billing-btn--primary" onClick={submit} disabled={busy}>
                        {busy ? 'Guardando…' : editing ? 'Guardar' : 'Crear'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
